import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';
import { BASE_URL } from '../../Api/ApiUrls';
import GuestLoginModal from '../../GuestLoginModal';
import { CircularProgress } from '@mui/material';
import SuccessAlert from '../../../Components/Users/Alertpage/SuccessAlert/SuccessAlert';
import FailureAlert from '../../../Components/Users/Alertpage/FailureAlert/FailureAlert';
import FullScreenLoader from '../../FullScreenLoader';

// Styled Components
const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': { backgroundColor: '#654321' },
}));

// Constants
const LOOKING_FOR_OPTIONS = ['Purchase', 'Rent', 'Lease', 'JV/JD', 'Build to Suit'];

const PROPERTY_TYPE_OPTIONS = {
  Purchase: ['Plot', 'Land', 'Rental with Duplex Building', 'Duplex House', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
  Rent: ['House', 'Duplex House', 'Villa', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
  Lease: ['House', 'Duplex House', 'Villa', 'Plot', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
  'JV/JD': ['Land', 'Plot'],
  'Build to Suit': ['Land', 'Plot']
};

const BUDGET_RANGES = {
  Purchase: ['No Min', '5L', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '5.5CR', '6CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR', '120CR', '140CR', '160CR', '190CR', '200CR', '250CR', '300CR', '350CR', '400CR', '450CR', '500CR', '550CR', '600CR', '650CR', '700CR', '750CR', '800CR', '850CR', '900CR', '1000CR', '1500CR', '2000CR', '2500CR', '3000CR', '4000CR', '5000CR', '6000CR', '7000CR', '8000CR', '9000CR', '10000CR+++'],

  Rent: ['No Min', '1K', '2K', '3K', '4K', '5K', '10K', '15K', '20K', '25K', '30K', '35K', '40K', '50K', '60K', '70K', '80K', '90K', '1L', '1.25L', '1.5L', '2L', '2.5L', '3L', '3.5L', '4L', '4.5L', '5L', '6L', '7L', '8L', '9L', '10L', '15L', '20L', '25L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.2CR', '1.4CR', '1.6CR', '1.8CR', '2CR', '2.2CR', '2.4CR', '2.6CR', '2.8CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '5.5CR', '6CR', '6.5CR', '7CR', '7.5CR', '8CR', '8.5CR', '9CR', '9.5CR', '10CR', '15CR', '20CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR+++'],

  Lease: ['No Min', '1L', '2L', '3L', '4L', '5L', '10L', '15L', '20L', '25L', '30L', '35L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '6CR', '7CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR+++'],

  'JV/JD': ['No Min', '5L', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '5.5CR', '6CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR', '120CR', '140CR', '160CR', '190CR', '200CR', '250CR', '300CR', '350CR', '400CR', '450CR', '500CR', '550CR', '600CR', '650CR', '700CR', '750CR', '800CR', '850CR', '900CR', '1000CR', '1500CR', '2000CR', '2500CR', '3000CR', '4000CR', '5000CR', '6000CR', '7000CR', '8000CR', '9000CR', '10000CR+++'],

  'Build to Suit': ['No Min', '5L', '10L', '20L', '30L', '40L', '50L', '60L', '70L', '80L', '90L', '1CR', '1.25CR', '1.5CR', '1.75CR', '2CR', '2.5CR', '3CR', '3.5CR', '4CR', '4.5CR', '5CR', '5.5CR', '6CR', '8CR', '9CR', '10CR', '15CR', '20CR', '25CR', '30CR', '40CR', '50CR', '60CR', '70CR', '80CR', '90CR', '100CR', '120CR', '140CR', '160CR', '190CR', '200CR', '250CR', '300CR', '350CR', '400CR', '450CR', '500CR', '550CR', '600CR', '650CR', '700CR', '750CR', '800CR', '850CR', '900CR', '1000CR', '1500CR', '2000CR', '2500CR', '3000CR', '4000CR', '5000CR', '6000CR', '7000CR', '8000CR', '9000CR', '10000CR+++'],
};



const INITIAL_FORM_DATA = {
  length: '', width: '', units: 'ft', area: '', no_of_bedrooms: '',
  min_monthly_rent: '', max_monthly_rent: '', min_lease_per_year: '', max_lease_per_year: '',
  min_acres: '', max_acres: '', budget_per_acre: '', ratio: '', floor: '', expected_rental_income: ''
};

// Numeric-only fields (digits + optional decimal point)
const NUMERIC_ONLY_FIELDS = [
  'length', 'width', 'area',
  'min_acres', 'max_acres', 'budget_per_acre',
  'floor', 'roadwidth',
  'min_monthly_rent', 'max_monthly_rent',
  'min_lease_per_year', 'max_lease_per_year',
  'expected_rental_income'
];

// Ratio fields — allow format like 60:40
const RATIO_FIELDS = ['ratio'];

// Helper Functions
const convertBudgetToNumber = (budgetStr) => {
  if (!budgetStr || budgetStr === 'No Min') return 0;
  if (budgetStr.includes('K')) return parseFloat(budgetStr) * 1000;
  if (budgetStr.includes('L')) return parseFloat(budgetStr) * 100000;
  if (budgetStr.includes('CR')) return parseFloat(budgetStr) * 10000000;
  if (budgetStr.includes('+++')) return parseFloat(budgetStr) * 10000000;
  return parseFloat(budgetStr);
};

const PGRoom = () => { 
  const navigate = useNavigate();
  const { userId, isGuest } = useContext(AuthContext);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  
  const [lookingFor, setLookingFor] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationDetails, setLocationDetails] = useState([]);
  const [propertyType, setPropertyType] = useState('');
  const [options, setOptions] = useState([]);
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);

  // ✅ Validation errors state
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isGuest) {
      setShowGuestModal(true);
    }
  }, [isGuest]);

  // Google Maps Script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const getAvailableMaxBudgets = () => {
    if (!lookingFor || !minBudget) return BUDGET_RANGES[lookingFor] || [];
    const allBudgets = BUDGET_RANGES[lookingFor];
    const minIndex = allBudgets.indexOf(minBudget);
    if (minIndex === -1) return allBudgets;
    return allBudgets.slice(minIndex + 1);
  };

  // Fetch geocoding for a location
  const getGeocode = async (locationName) => {
    return new Promise((resolve) => {
      if (!window.google) {
        resolve({ location: locationName, lat: "", long: "" });
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationName }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat().toFixed(4);
          const lng = results[0].geometry.location.lng().toFixed(4);
          resolve({ location: locationName, lat, long: lng });
        } else {
          resolve({ location: locationName, lat: "", long: "" });
        }
      });
    });
  };

  // Fetch Places with predictions
  const fetchPlaces = (input) => {
    if (!input || !window.google) return;
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input, types: ["geocode"], componentRestrictions: { country: "IN" } },
      (predictions) => {
        if (predictions) setOptions(predictions.map((p) => p.description));
      }
    );
  };

  // Handle location changes
  const handleLocationChange = async (event, newValue) => {
    setLocations(newValue);
    if (newValue.length > 0) {
      setErrors(prev => ({ ...prev, locations: '' }));
    }
    const locationPromises = newValue.map(loc => getGeocode(loc));
    const results = await Promise.all(locationPromises);
    setLocationDetails(results);
  };

  const handleFormDataChange = (field, value) => {
    if (NUMERIC_ONLY_FIELDS.includes(field)) {
      // Allow empty string or digits with optional single decimal point
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    }
    if (RATIO_FIELDS.includes(field)) {
      // Allow digits and a single colon e.g. 60:40
      if (value !== '' && !/^\d{0,3}:?\d{0,3}$/.test(value)) return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLookingForChange = (value) => {
    setLookingFor(value);
    setPropertyType('');
    setMinBudget('');
    setMaxBudget('');
    setFormData(INITIAL_FORM_DATA);
    setErrors(prev => ({ ...prev, lookingFor: '' }));
  };

  // ✅ Validate required fields before saving
  const validateForm = () => {
    const newErrors = {};

    if (!lookingFor)
      newErrors.lookingFor = "Please select a type";
    if (!propertyType)
      newErrors.propertyType = "Please select a property type";
    if (!locations || locations.length === 0)
      newErrors.locations = "Please add at least one location";
    if (!minBudget)
      newErrors.minBudget = "Please select a min budget";
    if (!maxBudget)
      newErrors.maxBudget = "Please select a max budget";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    console.log("📌 Saving property request...");

    // ✅ Block save if required fields are empty
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        looking_for: lookingFor.toLowerCase(),
        property_type: propertyType,
        length: parseFloat(formData.length) || 0,
        width: parseFloat(formData.width) || 0,
        units: formData.units,
        area: parseFloat(formData.area) || 0,
        min_budget: convertBudgetToNumber(minBudget),
        max_budget: convertBudgetToNumber(maxBudget),
        no_of_bedrooms: parseInt(formData.no_of_bedrooms) || 0,
        min_monthly_rent: parseFloat(formData.min_monthly_rent) || 0,
        max_monthly_rent: parseFloat(formData.max_monthly_rent) || 0,
        min_year_lease: parseFloat(formData.min_lease_per_year) || 0,
        max_year_lease: parseFloat(formData.max_lease_per_year) || 0,
        min_acres: parseFloat(formData.min_acres) || 0,
        max_acres: parseFloat(formData.max_acres) || 0,
        ratio: formData.ratio,
        floor: parseInt(formData.floor) || 0,
        comment: "Property request from form",
        user_id: userId,
        new_locations: locationDetails
      };

      console.log("📤 Payload:", JSON.stringify(payload, null, 2));

      const response = await fetch(`${BASE_URL}/property-request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("✅ Property request saved successfully:", result);
        setAlertMessage("Property request saved successfully!");
        setShowSuccess(true);
      } else {
        const errorText = await response.text();
        console.error("❌ Failed to save property request:", errorText);
        setAlertMessage("Failed to save property request. Please try again.");
        setShowFailure(true);
      }
    } catch (error) {
      console.error("🚨 Error:", error);
      setAlertMessage("An error occurred while saving. Please try again.");
      setShowFailure(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);
  const handleBackClick = () => navigate(-1);

  // Field Components
  const renderDimensionFields = () => (
    <Box key="dimensions" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Dimensions</Typography>
      <Stack direction="row" spacing={1}>
        <TextField fullWidth label="Length" placeholder="e.g., 40" variant="outlined" size="small"
          value={formData.length} onChange={(e) => handleFormDataChange('length', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }} />
        <Typography sx={{ alignSelf: 'center' }}>×</Typography>
        <TextField fullWidth label="Width" placeholder="e.g., 60" variant="outlined" size="small"
          value={formData.width} onChange={(e) => handleFormDataChange('width', e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }} />
        <TextField select label="Units" value={formData.units} size="small" sx={{ minWidth: 80 }}
          onChange={(e) => handleFormDataChange('units', e.target.value)}>
          <MenuItem value="ft">ft</MenuItem>
          <MenuItem value="m">m</MenuItem>
        </TextField>
      </Stack>
    </Box>
  );

  const renderRangeFields = (minField, maxField, label) => (
    <Box key={label} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{label}</Typography>
      <Stack direction="row" spacing={1}>
        <TextField fullWidth label={`Min ${label}`} placeholder="e.g., 10" variant="outlined" size="small"
          value={formData[minField]} onChange={(e) => handleFormDataChange(minField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }} />
        <TextField fullWidth label={`Max ${label}`} placeholder="e.g., 15" variant="outlined" size="small"
          value={formData[maxField]} onChange={(e) => handleFormDataChange(maxField, e.target.value)}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }} />
      </Stack>
    </Box>
  );

  const renderBedroomField = () => (
    <FormControl fullWidth key="bedrooms" sx={{ mb: 2 }} size="small">
      <InputLabel>Number of Bedrooms</InputLabel>
      <Select label="Number of Bedrooms" value={formData.no_of_bedrooms}
        onChange={(e) => handleFormDataChange('no_of_bedrooms', e.target.value)}>
        {[1, 2, 3, 4].map(num => <MenuItem key={num} value={num.toString()}>{num} BHK</MenuItem>)}
      </Select>
    </FormControl>
  );

  const renderTextField = (field, label, placeholder) => (
    <TextField key={field} fullWidth label={label} placeholder={placeholder} variant="outlined" size="small" sx={{ mb: 2 }}
      value={formData[field]} onChange={(e) => handleFormDataChange(field, e.target.value)}
      inputProps={
        NUMERIC_ONLY_FIELDS.includes(field)
          ? { inputMode: 'decimal', pattern: '[0-9]*' }
          : RATIO_FIELDS.includes(field)
          ? { inputMode: 'text' }
          : {}
      }
    />
  );

  // Dynamic Fields Logic
  const getDynamicFields = () => {
    if (!propertyType || !lookingFor) return null;

    const fields = [];
    const config = {
      Purchase: {
        dimensions: ['Plot', 'Rental with Duplex Building', 'Duplex House', 'Land', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        acres: ['Land'],
        budgetPerAcre: ['Land'],
        rentalIncome: ['PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
        bedrooms: ['Flat', 'Villas'],
        area:['Flat']
      },
      Rent: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        monthlyRent: ['*'],
        dimensions: ['Commercial Plot', 'Commercial Building', 'Commercial Land', 'PG', 'Office', 'Flat'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      Lease: {
        bedrooms: ['House', 'Duplex House', 'Villa'],
        leasePerYear: ['*'],
        dimensions: ['Plot', 'Commercial Building', 'Commercial Plot', 'Commercial Land', 'PG', 'Office', 'Flat'],
        monthlyRent: ['Commercial Plot', 'Commercial Land'],
        acres: ['Commercial Land'],
        area: ['Office', 'Flat']
      },
      'JV/JD': {
        acres: ['Land'],
        dimensions: ['Plot'],
        ratio: ['*']
      },
      'Build to Suit': {
        acres: ['Land'],
        dimensions: ['Plot'],
        floor: ['Plot']
      }
    };

    const currentConfig = config[lookingFor];
    if (!currentConfig) return fields;

    Object.entries(currentConfig).forEach(([fieldType, propertyTypes]) => {
      if (propertyTypes.includes('*') || propertyTypes.includes(propertyType)) {
        switch (fieldType) {
          case 'dimensions':
            fields.push(renderDimensionFields());
            break;
          case 'acres':
            fields.push(renderRangeFields('min_acres', 'max_acres', 'Acres'));
            break;
          case 'monthlyRent':
            fields.push(renderRangeFields('min_monthly_rent', 'max_monthly_rent', 'Monthly Rent'));
            break;
          case 'leasePerYear':
            fields.push(renderRangeFields('min_lease_per_year', 'max_lease_per_year', 'Lease per Year'));
            break;
          case 'budgetPerAcre':
            fields.push(renderTextField('budget_per_acre', 'Budget per Acre', 'e.g., 500000'));
            break;
          case 'rentalIncome':
            fields.push(renderTextField('expected_rental_income', 'Expected Rental Income', 'e.g., 50000'));
            break;
          case 'bedrooms':
            fields.push(renderBedroomField());
            break;
          case 'ratio':
            fields.push(renderTextField('ratio', 'Ratio', 'e.g., 60:40'));
            break;
          case 'floor':
            fields.push(renderTextField('floor', 'Floor', 'e.g., 3'));
            break;
          case 'area':
            fields.push(renderTextField('area', 'Area (sq ft)', 'e.g., 1200'));
            break;
          default:
            break;
        }
      }
    });

    return fields;
  };

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />
      <Box sx={{ 
        padding: '16px', 
        backgroundColor: '#f5f1eb', 
        minHeight: '100vh',
        marginTop: "50px",
        paddingBottom: '80px',
      }}>
        <FilterContainer>

          {/* ✅ Looking For */}
          <SectionTitle>Looking For</SectionTitle>
          <FormControl fullWidth sx={{ mb: 2 }} size="small" error={!!errors.lookingFor}>
            <InputLabel>Select Type *</InputLabel>
            <Select
              value={lookingFor}
              label="Select Type *"
              onChange={(e) => handleLookingForChange(e.target.value)}
            >
              {LOOKING_FOR_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
            {errors.lookingFor && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.lookingFor}
              </Typography>
            )}
          </FormControl>

          {/* ✅ Property Type */}
          <SectionTitle>Property Type</SectionTitle>
          <FormControl fullWidth sx={{ mb: 2 }} size="small" error={!!errors.propertyType}>
            <InputLabel>Select Property Type *</InputLabel>
            <Select
              value={propertyType}
              label="Select Property Type *"
              onChange={(e) => {
                setPropertyType(e.target.value);
                setErrors(prev => ({ ...prev, propertyType: '' }));
              }}
              disabled={!lookingFor}
            >
              {lookingFor && PROPERTY_TYPE_OPTIONS[lookingFor]?.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            {errors.propertyType && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.propertyType}
              </Typography>
            )}
          </FormControl>

          {/* ✅ Areas/Cities — required */}
          <SectionTitle>Areas/Cities</SectionTitle>
          <Autocomplete
            multiple
            freeSolo
            options={options}
            value={locations}
            onChange={handleLocationChange}
            onInputChange={(e, value) => fetchPlaces(value)}
            renderTags={(value, getTagProps) => value.map((option, index) => (
              <Chip
                variant="filled"
                label={option}
                {...getTagProps({ index })}
                sx={{ backgroundColor: "#8B4513", color: "white" }}
              />
            ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Areas / Colonies / Streets *"
                size="small"
                error={!!errors.locations}
                helperText={errors.locations || ''}
              />
            )}
          />

          {/* ✅ Budget Range */}
          <SectionTitle sx={{ mt: 2 }}>Budget Range</SectionTitle>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <FormControl fullWidth size="small" error={!!errors.minBudget}>
                <InputLabel>Min Budget *</InputLabel>
                <Select
                  value={minBudget}
                  label="Min Budget *"
                  onChange={(e) => {
                    setMinBudget(e.target.value);
                    setErrors(prev => ({ ...prev, minBudget: '' }));
                  }}
                  disabled={!lookingFor}
                >
                  {lookingFor && BUDGET_RANGES[lookingFor]?.map((budget, index) => (
                    <MenuItem key={index} value={budget}>{budget}</MenuItem>
                  ))}
                </Select>
                {errors.minBudget && (
                  <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                    {errors.minBudget}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth size="small" error={!!errors.maxBudget}>
                <InputLabel>Max Budget *</InputLabel>
                <Select
                  value={maxBudget}
                  label="Max Budget *"
                  onChange={(e) => {
                    setMaxBudget(e.target.value);
                    setErrors(prev => ({ ...prev, maxBudget: '' }));
                  }}
                  disabled={!lookingFor || !minBudget}
                >
                  {getAvailableMaxBudgets().map((budget, index) => (
                    <MenuItem key={index} value={budget}>{budget}</MenuItem>
                  ))}
                </Select>
                {errors.maxBudget && (
                  <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                    {errors.maxBudget}
                  </Typography>
                )}
              </FormControl>
            </Stack>
          </Box>

          {getDynamicFields()}

          <>
            {/* Full Screen Loader */}
            <FullScreenLoader open={loading} text="Saving changes..." />

            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancel}
                disabled={loading}
                sx={{
                  borderRadius: "8px",
                  borderColor: "#8B4513",
                  color: "#8B4513",
                  "&:hover": {
                    borderColor: "#654321",
                    backgroundColor: "rgba(139, 69, 19, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>

              <StyledButton
                fullWidth
                onClick={handleSave}
                disabled={isGuest || loading}
                sx={{
                  backgroundColor: (isGuest || loading) ? "#ccc" : undefined,
                  color: (isGuest || loading) ? "#777" : undefined,
                  cursor: (isGuest || loading) ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: (isGuest || loading) ? "#ccc" : undefined,
                  },
                }}
              >
                {isGuest ? "Login Required" : "Save"}
              </StyledButton>
            </Stack>
          </>

        </FilterContainer>
      </Box>

      <GuestLoginModal
        open={showGuestModal}
        onClose={() => setShowGuestModal(false)}
      />

      {showSuccess && (
        <SuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowSuccess(false);
            navigate("/dashboard");
          }}
        />
      )}

      {showFailure && (
        <FailureAlert
          message={alertMessage}
          onClose={() => {
            console.log("Closing failure alert");
            setShowFailure(false);
            setAlertMessage("");
            navigate("/pg-hostel-form");
          }}
        />
      )}

      <FormsBottomNavbar />
    </>
  );
};

export default PGRoom;